import { useState, useEffect, useCallback, useMemo } from "react";
import { Layout } from "@/components/Layout";
import { Activity, Radio, AlertTriangle, Wifi, RefreshCw, TrendingUp, Volume2, Brain, Shield, Folder, FolderOpen, ChevronRight, ChevronDown, History, Database, Lightbulb } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { dashboardAPI, systemsAPI, scansAPI, equipmentAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DashboardPredictionWidget } from "@/components/RFPrediction";

const StatusIndicator = ({ status }) => {
  const statusConfig = {
    stable: { class: "bg-[#34C759]", shadow: "shadow-[0_0_8px_rgba(52,199,89,0.5)]" },
    warning: { class: "bg-[#FFCC00]", shadow: "shadow-[0_0_8px_rgba(255,204,0,0.5)]" },
    interference: { class: "bg-[#FF3B30]", shadow: "shadow-[0_0_8px_rgba(255,59,48,0.5)]" },
  };
  const config = statusConfig[status] || statusConfig.stable;
  return <div className={`w-3 h-3 rounded-full ${config.class} ${config.shadow}`} />;
};

const KPICard = ({ label, value, icon: Icon, color, trend, compact = false }) => (
  <div 
    className={`bg-[#111111] border border-white/[0.08] rounded-lg transition-all hover:border-white/20 ${
      compact ? "p-3" : "p-4"
    }`}
    data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="flex items-center justify-between">
      <div className="min-w-0 flex-1">
        <p className={`text-[#71717A] truncate ${compact ? "text-[10px]" : "text-xs"}`}>{label}</p>
        <p className={`font-bold font-mono ${compact ? "text-xl" : "text-2xl"}`} style={{ color }}>
          {value}
        </p>
        {trend && (
          <div className="flex items-center gap-1 mt-1">
            <TrendingUp className="w-3 h-3 text-[#34C759]" />
            <span className="text-[10px] text-[#34C759]">{trend}</span>
          </div>
        )}
      </div>
      <div
        className={`rounded-lg flex items-center justify-center flex-shrink-0 ${
          compact ? "w-8 h-8" : "w-10 h-10"
        }`}
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className={compact ? "w-4 h-4" : "w-5 h-5"} style={{ color }} />
      </div>
    </div>
  </div>
);

// Component for grouped systems view by Brand > Model
const SystemsGroupedView = ({ systems, compact = false, alertSystems = {} }) => {
  const [expandedBrands, setExpandedBrands] = useState({});
  const [expandedModels, setExpandedModels] = useState({});

  // Get alert status for a system based on frequency
  const getSystemAlertStatus = (system) => {
    if (!system.frequency) return null;
    const freqKey = system.frequency.toFixed(3);
    return alertSystems[freqKey] || alertSystems[system.id] || null;
  };

  // Group systems by brand and model
  const groupedSystems = useMemo(() => {
    const groups = {};
    
    systems.forEach(system => {
      // Extract brand from model or equipment_id
      let brand = "Otros";
      let model = system.model || "Sin modelo";
      
      // Try to extract brand from model name
      const modelLower = (system.model || "").toLowerCase();
      if (modelLower.includes("sennheiser") || modelLower.includes("ew ") || modelLower.includes("ew-d") || modelLower.includes("sr 2050")) {
        brand = "Sennheiser";
      } else if (modelLower.includes("shure") || modelLower.includes("psm") || modelLower.includes("qlxd") || modelLower.includes("ulxd")) {
        brand = "Shure";
      } else if (modelLower.includes("anleon") || modelLower.includes("s3d") || modelLower.includes("s3")) {
        brand = "Anleon";
      } else if (modelLower.includes("relacart") || modelLower.includes("pm-320")) {
        brand = "Relacart";
      } else if (modelLower.includes("audio-technica") || modelLower.includes("at-")) {
        brand = "Audio-Technica";
      }
      
      // Clean model name (remove brand prefix if present)
      model = model.replace(/^(sennheiser|shure|anleon|relacart|audio-technica)\s*/i, "").trim() || model;
      
      if (!groups[brand]) {
        groups[brand] = {};
      }
      if (!groups[brand][model]) {
        groups[brand][model] = [];
      }
      groups[brand][model].push(system);
    });
    
    return groups;
  }, [systems]);

  const toggleBrand = (brand) => {
    setExpandedBrands(prev => ({ ...prev, [brand]: !prev[brand] }));
  };

  const toggleModel = (brand, model) => {
    const key = `${brand}-${model}`;
    setExpandedModels(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Initialize all brands and models as collapsed by default
  // No useEffect needed - empty objects mean all collapsed

  const brandColors = {
    "Sennheiser": "#1428A0",
    "Shure": "#D4AF37",
    "Anleon": "#34C759",
    "Relacart": "#FF6B00",
    "Audio-Technica": "#E31837",
    "Otros": "#71717A"
  };

  return (
    <div className="space-y-2">
      {Object.entries(groupedSystems).sort().map(([brand, models]) => {
        const brandExpanded = expandedBrands[brand] === true;
        const totalSystems = Object.values(models).reduce((sum, arr) => sum + arr.length, 0);
        const brandColor = brandColors[brand] || brandColors["Otros"];
        
        return (
          <div key={brand} className="bg-[#111111] border border-white/[0.08] rounded-lg overflow-hidden">
            {/* Brand Header */}
            <button
              onClick={() => toggleBrand(brand)}
              className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-2">
                {brandExpanded ? (
                  <FolderOpen className="w-5 h-5" style={{ color: brandColor }} />
                ) : (
                  <Folder className="w-5 h-5" style={{ color: brandColor }} />
                )}
                <span className="font-semibold text-[#F4F4F5]">{brand}</span>
                <span className="text-xs text-[#71717A] bg-white/5 px-2 py-0.5 rounded">
                  {totalSystems} sistema{totalSystems !== 1 ? "s" : ""}
                </span>
              </div>
              {brandExpanded ? (
                <ChevronDown className="w-4 h-4 text-[#71717A]" />
              ) : (
                <ChevronRight className="w-4 h-4 text-[#71717A]" />
              )}
            </button>
            
            {/* Models within brand */}
            {brandExpanded && (
              <div className="border-t border-white/[0.05]">
                {Object.entries(models).sort().map(([model, modelSystems]) => {
                  const modelKey = `${brand}-${model}`;
                  const modelExpanded = expandedModels[modelKey] === true;
                  
                  return (
                    <div key={modelKey} className="border-b border-white/[0.03] last:border-b-0">
                      {/* Model Header */}
                      <button
                        onClick={() => toggleModel(brand, model)}
                        className="w-full flex items-center justify-between p-2 pl-8 hover:bg-white/5 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          {modelExpanded ? (
                            <ChevronDown className="w-3 h-3 text-[#71717A]" />
                          ) : (
                            <ChevronRight className="w-3 h-3 text-[#71717A]" />
                          )}
                          <Radio className="w-4 h-4 text-[#71717A]" />
                          <span className="text-sm text-[#A1A1AA]">{model}</span>
                          <span className="text-[10px] text-[#71717A]">
                            ({modelSystems.length})
                          </span>
                        </div>
                      </button>
                      
                      {/* Systems within model */}
                      {modelExpanded && (
                        <div className="pl-12 pr-3 pb-2 space-y-1">
                          {modelSystems.map(system => {
                            const alert = getSystemAlertStatus(system);
                            const alertBorderClass = alert?.level === 'critical' 
                              ? 'border-[#FF3B30] bg-[#FF3B30]/5' 
                              : alert?.level === 'warning' 
                                ? 'border-[#FFCC00] bg-[#FFCC00]/5' 
                                : 'border-white/[0.05]';
                            
                            return (
                              <div 
                                key={system.id}
                                className={`flex flex-col p-2 bg-[#0A0A0A] rounded border transition-all ${alertBorderClass}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <StatusIndicator status={
                                      alert?.level === 'critical' ? 'interference' :
                                      alert?.level === 'warning' ? 'warning' :
                                      system.is_active ? "stable" : "warning"
                                    } />
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-[#F4F4F5] truncate">{system.name}</p>
                                      <p className="text-[10px] text-[#71717A] font-mono">
                                        {system.frequency?.toFixed(3)} MHz | {system.channel_group}{system.channel_number}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {alert && (
                                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                                        alert.level === 'critical' 
                                          ? 'bg-[#FF3B30]/20 text-[#FF3B30]' 
                                          : 'bg-[#FFCC00]/20 text-[#FFCC00]'
                                      }`}>
                                        {alert.level === 'critical' ? 'CRÍTICO' : 'ALERTA'}
                                      </span>
                                    )}
                                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                                      system.is_active 
                                        ? "bg-[#34C759]/20 text-[#34C759]" 
                                        : "bg-white/5 text-[#71717A]"
                                    }`}>
                                      {system.is_active ? "ON" : "OFF"}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Alert details and suggestion */}
                                {alert && (
                                  <div className={`mt-2 pt-2 border-t ${
                                    alert.level === 'critical' ? 'border-[#FF3B30]/30' : 'border-[#FFCC00]/30'
                                  }`}>
                                    <p className={`text-[10px] mb-1 ${
                                      alert.level === 'critical' ? 'text-[#FF3B30]' : 'text-[#FFCC00]'
                                    }`}>
                                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                                      {alert.reason || 'Posible interferencia detectada'}
                                    </p>
                                    
                                    {/* Equipment-based suggestion */}
                                    {alert.suggestion ? (
                                      <div className="p-1.5 bg-[#34C759]/10 border border-[#34C759]/30 rounded mt-1">
                                        <div className="flex items-center gap-1 text-[10px] text-[#34C759]">
                                          <Lightbulb className="w-3 h-3" />
                                          <span className="font-medium">{alert.suggestion.frequency.toFixed(3)} MHz</span>
                                        </div>
                                        <p className="text-[9px] text-[#A1A1AA] mt-0.5">
                                          Grupo {alert.suggestion.group} • CH {alert.suggestion.channel}
                                        </p>
                                        <p className="text-[8px] text-[#71717A] truncate">
                                          {alert.suggestion.equipment}
                                        </p>
                                      </div>
                                    ) : alert.suggestedFrequency ? (
                                      <div className="flex items-center gap-1 mt-1">
                                        <span className="text-[10px] text-[#34C759]">
                                          Sugerencia: {alert.suggestedFrequency.toFixed(3)} MHz
                                        </span>
                                        <span className="text-[8px] text-[#71717A]">(limpio)</span>
                                      </div>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Original SystemCard component (kept for backwards compatibility)
const SystemCard = ({ system, compact = false }) => (
  <div 
    className={`bg-[#111111] border border-white/[0.08] rounded-lg hover:border-[#34C759]/30 transition-all ${
      compact ? "p-3" : "p-4"
    }`}
    data-testid={`system-card-${system.id}`}
  >
    {/* Header */}
    <div className="flex items-start justify-between gap-2 mb-3">
      <div className="min-w-0 flex-1">
        <p className={`font-semibold text-[#F4F4F5] truncate ${compact ? "text-sm" : "text-base"}`}>
          {system.name}
        </p>
        <p className="text-[10px] text-[#71717A] truncate">{system.model}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
          system.is_active 
            ? "bg-[#34C759]/20 text-[#34C759]" 
            : "bg-white/5 text-[#71717A]"
        }`}>
          {system.is_active ? "ON" : "OFF"}
        </span>
        <StatusIndicator status={system.status} />
      </div>
    </div>

    {/* Frequency Display */}
    <div className={`font-mono font-bold text-[#34C759] mb-3 ${compact ? "text-xl" : "text-2xl"}`}>
      {system.frequency.toFixed(3)}
      <span className="text-xs text-[#71717A] ml-1">MHz</span>
    </div>

    {/* Channel Info */}
    <div className="flex items-center gap-3 mb-3 text-[10px] text-[#71717A]">
      <span className="bg-white/5 px-2 py-0.5 rounded">CH {system.channel}</span>
      <span className="bg-white/5 px-2 py-0.5 rounded">Grupo {system.channel_group || "A"}</span>
    </div>

    {/* Meters */}
    <div className="space-y-2">
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-[#71717A] flex items-center gap-1">
            <Wifi className="w-3 h-3" /> RF
          </span>
          <span className="text-[10px] font-mono text-[#F4F4F5]">{system.rf_level}%</span>
        </div>
        <div className="h-1.5 bg-[#0A0A0A] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#34C759] to-[#4ADE80] rounded-full transition-all duration-300"
            style={{ width: `${system.rf_level}%` }}
          />
        </div>
      </div>
      
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-[#71717A] flex items-center gap-1">
            <Volume2 className="w-3 h-3" /> AF
          </span>
          <span className="text-[10px] font-mono text-[#F4F4F5]">{system.af_level}%</span>
        </div>
        <div className="h-1.5 bg-[#0A0A0A] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] rounded-full transition-all duration-300"
            style={{ width: `${system.af_level}%` }}
          />
        </div>
      </div>
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-3 shadow-xl">
        <p className="font-mono text-sm text-[#34C759]">{label} MHz</p>
        <p className="font-mono text-xs text-[#A1A1AA]">
          Level: {payload[0].value} dBm
        </p>
        {payload[0].payload.is_occupied && (
          <p className="text-xs text-[#FFCC00] mt-1">
            {payload[0].payload.system_name}
          </p>
        )}
      </div>
    );
  }
  return null;
};

export default function DashboardPage({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [systems, setSystems] = useState([]);
  const [spectrum, setSpectrum] = useState([]);
  const [savedScans, setSavedScans] = useState([]);
  const [selectedScanId, setSelectedScanId] = useState("live");
  const [alertSystems, setAlertSystems] = useState({});
  const [equipmentList, setEquipmentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch equipment list on mount
  useEffect(() => {
    const fetchEquipment = async () => {
      try {
        const res = await equipmentAPI.getAll();
        const equipData = res.data?.equipment || res.data || [];
        setEquipmentList(Array.isArray(equipData) ? equipData : []);
      } catch (error) {
        console.error("Error fetching equipment:", error);
      }
    };
    fetchEquipment();
  }, []);

  // Analyze spectrum and find problematic frequencies
  const analyzeSpectrum = useCallback((spectrumData, systemsList) => {
    const alerts = {};
    const occupiedFreqs = systemsList.map(s => s.frequency).filter(Boolean);
    
    // Get average signal level
    const avgLevel = spectrumData.reduce((sum, p) => sum + p.level, 0) / spectrumData.length;
    const threshold = avgLevel + 20; // Consider high if 20dB above average
    
    // Helper to find suggestion from same equipment
    const findSuggestionFromEquipment = (system) => {
      // Find the equipment for this system
      const equipment = equipmentList.find(e => e.id === system.equipment_id);
      if (!equipment || !equipment.preset_frequencies) {
        return null;
      }
      
      // Search presets for an unused frequency
      const suggestions = [];
      for (const [group, channels] of Object.entries(equipment.preset_frequencies)) {
        for (const [channel, freq] of Object.entries(channels)) {
          const presetFreq = parseFloat(freq);
          if (isNaN(presetFreq)) continue;
          
          // Check if in band range and not used
          const inBand = presetFreq >= (system.band_min || 470) && presetFreq <= (system.band_max || 698);
          const isUsed = occupiedFreqs.some(f => Math.abs(f - presetFreq) < 0.05);
          
          if (inBand && !isUsed) {
            suggestions.push({
              frequency: presetFreq,
              group,
              channel,
              equipment: equipment.full_name || `${equipment.brand} ${equipment.model}`
            });
          }
        }
      }
      
      // Sort by proximity to original frequency
      suggestions.sort((a, b) => 
        Math.abs(a.frequency - system.frequency) - Math.abs(b.frequency - system.frequency)
      );
      
      return suggestions.length > 0 ? suggestions[0] : null;
    };
    
    // Find frequencies with high interference based on spectrum
    systemsList.forEach(system => {
      if (!system.frequency) return;
      
      // Find spectrum points near this system's frequency
      const nearbyPoints = spectrumData.filter(point => 
        Math.abs(point.frequency - system.frequency) < 0.3
      );
      
      if (nearbyPoints.length > 0) {
        const maxLevel = Math.max(...nearbyPoints.map(p => p.level));
        
        // Check for interference
        if (maxLevel > threshold || maxLevel > -35) {
          // First try to find from same equipment presets
          const equipmentSuggestion = findSuggestionFromEquipment(system);
          
          alerts[system.frequency.toFixed(3)] = {
            level: maxLevel > -25 ? 'critical' : 'warning',
            reason: `Señal alta: ${maxLevel.toFixed(0)} dBm`,
            suggestedFrequency: equipmentSuggestion?.frequency || findCleanFrequency(spectrumData, occupiedFreqs, system.frequency),
            suggestion: equipmentSuggestion, // Full suggestion with equipment info
            systemName: system.name,
            systemModel: system.model
          };
        }
      }
    });
    
    // Also check for intermodulation risks (systems too close together)
    for (let i = 0; i < systemsList.length; i++) {
      for (let j = i + 1; j < systemsList.length; j++) {
        const sys1 = systemsList[i];
        const sys2 = systemsList[j];
        if (sys1.frequency && sys2.frequency) {
          const diff = Math.abs(sys1.frequency - sys2.frequency);
          if (diff < 0.25 && diff > 0) { // Within 250 kHz
            // Mark both as warnings if not already critical
            const key1 = sys1.frequency.toFixed(3);
            const key2 = sys2.frequency.toFixed(3);
            
            if (!alerts[key1] || alerts[key1].level !== 'critical') {
              const equipmentSuggestion1 = findSuggestionFromEquipment(sys1);
              alerts[key1] = {
                level: 'warning',
                reason: `Muy cerca de ${sys2.name} (${diff.toFixed(3)} MHz)`,
                suggestedFrequency: equipmentSuggestion1?.frequency || findCleanFrequency(spectrumData, occupiedFreqs, sys1.frequency),
                suggestion: equipmentSuggestion1,
                systemName: sys1.name,
                systemModel: sys1.model
              };
            }
            if (!alerts[key2] || alerts[key2].level !== 'critical') {
              const equipmentSuggestion2 = findSuggestionFromEquipment(sys2);
              alerts[key2] = {
                level: 'warning',
                reason: `Muy cerca de ${sys1.name} (${diff.toFixed(3)} MHz)`,
                suggestedFrequency: equipmentSuggestion2?.frequency || findCleanFrequency(spectrumData, occupiedFreqs, sys2.frequency),
                suggestion: equipmentSuggestion2,
                systemName: sys2.name,
                systemModel: sys2.model
              };
            }
          }
        }
      }
    }
    
    return alerts;
  }, [equipmentList]);

  // Find a clean frequency based on spectrum
  const findCleanFrequency = (spectrumData, occupiedFreqs, currentFreq) => {
    // Look for frequencies with low signal levels
    const cleanPoints = spectrumData
      .filter(p => p.level < -70) // Low signal
      .filter(p => !occupiedFreqs.some(f => Math.abs(f - p.frequency) < 0.5)) // Not near occupied
      .filter(p => Math.abs(p.frequency - currentFreq) > 0.5) // At least 500 kHz away
      .sort((a, b) => a.level - b.level); // Sort by lowest signal first
    
    if (cleanPoints.length > 0) {
      // Return the frequency with lowest interference
      return cleanPoints[0].frequency;
    }
    return null;
  };

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [statsRes, systemsRes, spectrumRes, scansRes] = await Promise.all([
        dashboardAPI.getStats(),
        systemsAPI.getAll(),
        dashboardAPI.getSpectrum(),
        scansAPI.getAll(),
      ]);
      setStats(statsRes.data);
      setSystems(systemsRes.data);
      // scansAPI returns array directly
      setSavedScans(Array.isArray(scansRes.data) ? scansRes.data : []);
      
      // Use live spectrum by default
      if (selectedScanId === "live") {
        setSpectrum(spectrumRes.data.spectrum);
        
        // Analyze spectrum for alerts
        if (spectrumRes.data.spectrum?.length > 0) {
          const alerts = analyzeSpectrum(spectrumRes.data.spectrum, systemsRes.data);
          setAlertSystems(alerts);
        }
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [analyzeSpectrum, selectedScanId]);

  // Handle scan selection change
  const handleScanChange = useCallback((scanId) => {
    setSelectedScanId(scanId);
    
    if (scanId === "live") {
      // Reload live spectrum
      dashboardAPI.getSpectrum().then(res => {
        setSpectrum(res.data.spectrum);
        if (res.data.spectrum?.length > 0) {
          const alerts = analyzeSpectrum(res.data.spectrum, systems);
          setAlertSystems(alerts);
        }
      });
    } else {
      // Load spectrum from saved scan
      const selectedScan = savedScans.find(s => s.id === scanId);
      if (selectedScan && selectedScan.scan_data) {
        // Convert scan data format to spectrum format
        const scanSpectrum = selectedScan.scan_data.map(point => ({
          frequency: point.frequency,
          level: point.level,
          is_occupied: systems.some(s => 
            s.frequency && Math.abs(s.frequency - point.frequency) < 0.1
          ),
          system_name: systems.find(s => 
            s.frequency && Math.abs(s.frequency - point.frequency) < 0.1
          )?.name || null
        }));
        setSpectrum(scanSpectrum);
        
        // Analyze the saved scan for alerts
        if (scanSpectrum.length > 0) {
          const alerts = analyzeSpectrum(scanSpectrum, systems);
          setAlertSystems(alerts);
        }
      }
    }
  }, [savedScans, systems, analyzeSpectrum]);

  useEffect(() => {
    fetchData();
    // Only auto-refresh if viewing live spectrum
    if (selectedScanId === "live") {
      const interval = setInterval(() => fetchData(false), 30000);
      return () => clearInterval(interval);
    }
  }, [fetchData, selectedScanId]);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-[#34C759] border-t-transparent rounded-full animate-spin" />
            <span className="text-[#71717A] text-sm">Cargando dashboard...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      {/* Header - Mobile optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="font-heading text-lg sm:text-xl font-bold text-[#F4F4F5]">
            Dashboard
          </h1>
          <p className="text-xs sm:text-sm text-[#71717A]">
            Monitoreo en tiempo real de sistemas RF
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-[#71717A] bg-[#111111] px-3 py-1.5 rounded-full border border-white/[0.08]">
            <div className="w-2 h-2 rounded-full bg-[#34C759] animate-pulse" />
            <span className="hidden sm:inline">EN VIVO</span>
            <span className="sm:hidden">LIVE</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="h-8 px-3 text-[#71717A] hover:text-white"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* KPIs - Mobile optimized grid */}
      <div 
        className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6" 
        data-testid="kpi-grid"
      >
        <KPICard
          label="Sistemas"
          value={stats?.total_systems || 0}
          icon={Radio}
          color="#F4F4F5"
          compact={isMobile}
        />
        <KPICard
          label="Activos"
          value={stats?.active_systems || 0}
          icon={Activity}
          color="#34C759"
          compact={isMobile}
        />
        <KPICard
          label="Alertas"
          value={stats?.alerts || 0}
          icon={AlertTriangle}
          color={stats?.alerts > 0 ? "#FFCC00" : "#71717A"}
          compact={isMobile}
        />
        <KPICard
          label="RF Prom."
          value={`${stats?.average_rf_level || 0}%`}
          icon={Wifi}
          color="#34C759"
          compact={isMobile}
        />
      </div>

      {/* RF Prediction Widget - Full width row */}
      <div className="mb-4 sm:mb-6">
        <DashboardPredictionWidget />
      </div>

      {/* Spectrum Chart - Full width */}
      <div 
        className="bg-[#111111] border border-white/[0.08] rounded-lg p-3 sm:p-4 mb-4 sm:mb-6" 
        data-testid="spectrum-chart"
      >
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-3 sm:mb-4 pb-3 border-b border-white/[0.08]">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="font-heading font-semibold text-sm sm:text-base text-[#F4F4F5]">
                Espectro RF
              </h2>
              <p className="text-[10px] sm:text-xs text-[#71717A]">
                {selectedScanId === "live" ? "En vivo - Banda 560-674 MHz" : `Escaneo guardado`}
              </p>
            </div>
            
            {/* Scan Selector */}
            <div className="flex items-center gap-2">
              <Select value={selectedScanId} onValueChange={handleScanChange}>
                <SelectTrigger className="w-[180px] h-8 text-xs bg-[#0A0A0A] border-white/10">
                  <SelectValue placeholder="Seleccionar fuente..." />
                </SelectTrigger>
                <SelectContent className="bg-[#111111] border-white/10">
                  <SelectItem value="live">
                    <div className="flex items-center gap-2">
                      <Activity className="w-3 h-3 text-[#34C759]" />
                      <span>En Vivo</span>
                    </div>
                  </SelectItem>
                  {savedScans.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-[10px] text-[#71717A] border-t border-white/10 mt-1">
                        <History className="w-3 h-3 inline mr-1" />
                        Escaneos Guardados
                      </div>
                      {savedScans.map(scan => (
                        <SelectItem key={scan.id} value={scan.id}>
                          <div className="flex items-center gap-2">
                            <Database className="w-3 h-3 text-blue-400" />
                            <span className="truncate max-w-[120px]">{scan.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
              
              {selectedScanId !== "live" && (
                <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">
                  Histórico
                </Badge>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3 sm:gap-4 text-[10px] sm:text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-sm bg-[#34C759]" />
              <span className="text-[#71717A]">Señal</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-sm bg-[#FF3B30]/30" />
              <span className="text-[#71717A]">Ruido</span>
            </div>
          </div>
        </div>

        {/* Chart height adapts to screen - taller now */}
        <div className="h-56 sm:h-72 lg:h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={spectrum}
              margin={{ 
                top: 10, 
                right: isMobile ? 10 : 20, 
                left: isMobile ? -20 : -10, 
                bottom: 0 
              }}
            >
              <defs>
                <linearGradient id="rfGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#34C759" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#34C759" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.05)"
                vertical={false}
              />
              <XAxis
                dataKey="frequency"
                tick={{ fill: "#71717A", fontSize: isMobile ? 9 : 11, fontFamily: "JetBrains Mono" }}
                tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                tickFormatter={(v) => `${v}`}
                interval={isMobile ? 25 : 15}
              />
              <YAxis
                tick={{ fill: "#71717A", fontSize: isMobile ? 9 : 11, fontFamily: "JetBrains Mono" }}
                tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                domain={[-100, -20]}
                tickFormatter={(v) => `${v}`}
                width={isMobile ? 30 : 40}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine
                y={-85}
                stroke="#FF3B30"
                strokeDasharray="5 5"
                strokeOpacity={0.5}
              />
              <Area
                type="monotone"
                dataKey="level"
                stroke="#34C759"
                fill="url(#rfGradient)"
                strokeWidth={isMobile ? 1.5 : 2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Systems Grouped by Brand/Model */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h2 className="font-heading font-semibold text-sm sm:text-base text-[#F4F4F5]">
            Sistemas por Marca y Modelo
          </h2>
          {systems.length > 0 && (
            <span className="text-[10px] sm:text-xs text-[#71717A] bg-white/5 px-2 py-1 rounded">
              {systems.length} sistema{systems.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        
        {systems.length > 0 ? (
          <SystemsGroupedView systems={systems} compact={isMobile} alertSystems={alertSystems} />
        ) : (
          <div className="text-center py-8 sm:py-12 bg-[#111111] border border-white/[0.08] rounded-lg">
            <Radio className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-[#71717A] opacity-30" />
            <p className="text-[#71717A] text-sm sm:text-base">No hay sistemas registrados</p>
            <p className="text-xs sm:text-sm text-[#71717A]/70 mt-1">
              Ve a "Sistemas In-Ear" para agregar equipos
            </p>
          </div>
        )}
      </div>

      {/* Quick Stats Footer - Mobile only */}
      {isMobile && systems.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#0A0A0A]/95 backdrop-blur-md border-t border-white/[0.08] p-3 z-40">
          <div className="flex items-center justify-around text-center">
            <div>
              <p className="text-lg font-bold text-[#34C759]">{stats?.active_systems || 0}</p>
              <p className="text-[9px] text-[#71717A]">Activos</p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div>
              <p className="text-lg font-bold text-[#F4F4F5]">{stats?.average_rf_level || 0}%</p>
              <p className="text-[9px] text-[#71717A]">RF Prom.</p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div>
              <p className={`text-lg font-bold ${stats?.alerts > 0 ? "text-[#FFCC00]" : "text-[#71717A]"}`}>
                {stats?.alerts || 0}
              </p>
              <p className="text-[9px] text-[#71717A]">Alertas</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Spacer for mobile footer */}
      {isMobile && systems.length > 0 && <div className="h-16" />}
    </Layout>
  );
}
